import React, { Suspense, lazy } from "react";
import "@/App.css";
import "@/index.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { SiteContentProvider } from "@/context/SiteContentContext";
import { MaintenanceProvider } from "@/context/MaintenanceContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CartDrawer from "@/components/CartDrawer";
import CartTracker from "@/components/CartTracker";
import PiLauncher from "@/components/PiLauncher";
import WelcomePopup from "@/components/WelcomePopup";
import HolidayBanner from "@/components/HolidayBanner";
import LaunchCountdownBanner from "@/components/LaunchCountdownBanner";
import InstagramStamp from "@/components/InstagramStamp";
import GameMasterWidget from "@/components/GameMasterWidget";
import MaintenanceBanner from "@/components/MaintenanceBanner";
import Codebreaker from "@/components/Codebreaker";
import ScrollToTop from "@/components/ScrollToTop";
import AccessibilityToolbar from "@/components/AccessibilityToolbar";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import AnalyticsBeacon from "@/components/AnalyticsBeacon";
import { ProtectedRoute } from "@/components/ProtectedRoute";

// Code-splitting: defer non-critical pages to per-route chunks.
// Eager-load only the homepage so first-paint is instant; everything else
// streams in a small chunk on demand.
import HomePage from "@/pages/HomePage";
const ShopPage = lazy(() => import("@/pages/ShopPage"));
const ProductDetailPage = lazy(() => import("@/pages/ProductDetailPage"));
const CartPage = lazy(() => import("@/pages/CartPage"));
const CheckoutPage = lazy(() => import("@/pages/CheckoutPage"));
const CheckoutSuccessPage = lazy(() => import("@/pages/CheckoutSuccessPage"));
const CustomDesignPage = lazy(() => import("@/pages/CustomDesignPage"));
const CustomBriefBuilderPage = lazy(() => import("@/pages/CustomBriefBuilderPage"));
const ClubPage = lazy(() => import("@/pages/ClubPage"));
const ClubPlannerPage = lazy(() => import("@/pages/ClubPlannerPage"));
const ClubSuccessPage = lazy(() => import("@/pages/ClubSuccessPage"));
const AboutPage = lazy(() => import("@/pages/AboutPage"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const FaqPage = lazy(() => import("@/pages/FaqPage"));
const LegalPage = lazy(() => import("@/pages/LegalPage"));
const UnsubscribePage = lazy(() => import("@/pages/UnsubscribePage"));
const LoginPage = lazy(() => import("@/pages/LoginPage"));
const RegisterPage = lazy(() => import("@/pages/RegisterPage"));
const ForgotPasswordPage = lazy(() => import("@/pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("@/pages/ResetPasswordPage"));
const AccountPage = lazy(() => import("@/pages/AccountPage"));
const OrderDetailPage = lazy(() => import("@/pages/OrderDetailPage"));
const ArcadePage = lazy(() => import("@/pages/ArcadePage"));
const PlayPage = lazy(() => import("@/pages/PlayPage"));
const BrandInviteAcceptPage = lazy(() => import("@/pages/BrandInviteAcceptPage"));
const QuoteRequestPage = lazy(() => import("@/pages/QuotePageV2"));
const AdminPage = lazy(() => import("@/pages/AdminPage"));
const AuthCallbackPage = lazy(() => import("@/pages/AuthCallbackPage"));
const CollectionsPage = lazy(() => import("@/pages/CollectionsPage"));
const CollectionDetailPage = lazy(() => import("@/pages/CollectionDetailPage"));
const OverseasQuotePage = lazy(() => import("@/pages/OverseasQuotePage"));
const StyleQuizPage = lazy(() => import("@/pages/StyleQuizPage"));
const WishlistPage = lazy(() => import("@/pages/WishlistPage"));
const JournalIndexPage = lazy(() => import("@/pages/JournalPage").then((m) => ({ default: m.JournalIndexPage })));
const JournalDetailPage = lazy(() => import("@/pages/JournalPage").then((m) => ({ default: m.JournalDetailPage })));
const ShippingQuotePage = lazy(() => import("@/pages/ShippingQuotePage"));
const ReviewsPage = lazy(() => import("@/pages/ReviewsPage"));
const AccessoriesPage = lazy(() => import("@/pages/AccessoriesPage"));
const ShareYourStoryPage = lazy(() => import("@/pages/ShareYourStoryPage"));
const GiftCardsPage = lazy(() => import("@/pages/GiftCardsPage"));
const BulkOrdersPage = lazy(() => import("@/pages/BulkOrdersPage"));
const TradeAccountPage = lazy(() => import("@/pages/TradeAccountPage"));
const StartYourBrandPage = lazy(() => import("@/pages/StartYourBrandPage"));
const BrandPortalPage = lazy(() => import("@/pages/BrandPortalPage"));
const PublicBrandPage = lazy(() => import("@/pages/PublicBrandPage"));
const BrandsDirectoryPage = lazy(() => import("@/pages/BrandsDirectoryPage"));
const ShopTheFeedPage = lazy(() => import("@/pages/ShopTheFeedPage"));
const ShareLeaderboardPage = lazy(() => import("@/pages/ShareLeaderboardPage"));
const TheVaultPage = lazy(() => import("@/pages/TheVaultPage"));
const VoteDesignsPage = lazy(() => import("@/pages/VoteDesignsPage"));
const OurStudioPage = lazy(() => import("@/pages/OurStudioPage"));
import ExitIntentPopup from "@/components/ExitIntentPopup";
import ReferralCapture from "@/components/ReferralCapture";
import EmailHealthAlert from "@/components/admin/EmailHealthAlert";
import { Toaster } from "@/components/ui/sonner";

function RouteFallback() {
  return (
    <div className="min-h-[40vh] flex items-center justify-center text-sm text-black/40" data-testid="route-loading">
      Loading…
    </div>
  );
}

function Shell() {
  const location = useLocation();
  // Synchronously route to AuthCallback if returning from Google OAuth (hash has session_id)
  if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
    return <AuthCallbackPage />;
  }
  const isAdmin = location.pathname.startsWith("/admin") || location.pathname.startsWith("/studio-hq");
  const isArcade = location.pathname.startsWith("/arcade");
  const isPlay = location.pathname.startsWith("/play");
  const isCheckout = location.pathname.startsWith("/checkout");
  const isAuth = ["/login", "/register", "/forgot-password", "/reset-password", "/auth"].some((p) => location.pathname.startsWith(p));
  const isAccount = location.pathname.startsWith("/account");
  const hideChrome = isAdmin || isArcade || isPlay;
  const hideWelcomePopup = hideChrome || isCheckout || isAuth || isAccount;
  return (
    <>
      <a href="#main-content" className="skip-to-content" data-testid="skip-to-content">Skip to main content</a>
      {!isAdmin && !isPlay && <Header />}
      {!hideChrome && <LaunchCountdownBanner />}
      {!hideChrome && <MaintenanceBanner />}
      {!hideChrome && <HolidayBanner />}
      <ScrollToTop />
      <AnalyticsBeacon />
      <ReferralCapture />
      <main id="main-content" tabIndex="-1">
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/shop" element={<ShopPage />} />
          <Route path="/collections" element={<CollectionsPage />} />
          <Route path="/collection/:slug" element={<CollectionDetailPage />} />
          <Route path="/product/:id" element={<ProductDetailPage />} />
          <Route path="/cart" element={<CartPage />} />
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
          <Route path="/custom" element={<CustomDesignPage />} />
          <Route path="/custom-brief" element={<CustomBriefBuilderPage />} />
          <Route path="/bespoke" element={<CustomDesignPage defaultMode="bespoke" />} />
          <Route path="/bulk-orders" element={<BulkOrdersPage />} />
          <Route path="/start-your-brand" element={<StartYourBrandPage />} />
          <Route path="/brand-portal" element={<ProtectedRoute><BrandPortalPage /></ProtectedRoute>} />
          <Route path="/brands" element={<BrandsDirectoryPage />} />
          <Route path="/brands/:slug" element={<PublicBrandPage />} />
          <Route path="/shop-the-feed" element={<ShopTheFeedPage />} />
          <Route path="/share-leaderboard" element={<ShareLeaderboardPage />} />
          <Route path="/the-vault" element={<TheVaultPage />} />
          <Route path="/vote" element={<VoteDesignsPage />} />
          <Route path="/trade-account" element={<TradeAccountPage />} />
          <Route path="/trade" element={<TradeAccountPage />} />
          <Route path="/our-studio" element={<OurStudioPage />} />
          <Route path="/club" element={<ClubPage />} />
          <Route path="/club/planner" element={<ProtectedRoute><ClubPlannerPage /></ProtectedRoute>} />
          <Route path="/club/success" element={<ClubSuccessPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/faq" element={<FaqPage />} />
          <Route path="/terms" element={<LegalPage slug="terms" defaultTitle="Terms & Conditions" />} />
          <Route path="/privacy" element={<LegalPage slug="privacy" defaultTitle="Privacy Policy" />} />
          <Route path="/unsubscribe" element={<UnsubscribePage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/auth/callback" element={<AuthCallbackPage />} />
          <Route path="/account" element={<ProtectedRoute><AccountPage /></ProtectedRoute>} />
          <Route path="/account/orders/:id" element={<ProtectedRoute><OrderDetailPage /></ProtectedRoute>} />
          <Route path="/wishlist" element={<ProtectedRoute><WishlistPage /></ProtectedRoute>} />
          <Route path="/journal" element={<JournalIndexPage />} />
          <Route path="/journal/:slug" element={<JournalDetailPage />} />
          <Route path="/reviews" element={<ReviewsPage />} />
          <Route path="/accessories" element={<AccessoriesPage />} />
          <Route path="/share-your-story" element={<ShareYourStoryPage />} />
          <Route path="/gift-cards" element={<GiftCardsPage />} />
          <Route path="/gift-cards/success" element={<GiftCardsPage />} />
          <Route path="/shipping-quote" element={<ShippingQuotePage />} />
          <Route path="/arcade" element={<ArcadePage />} />
          <Route path="/play" element={<PlayPage />} />
          <Route path="/play/:code" element={<PlayPage />} />
          <Route path="/brand-invite/:token" element={<BrandInviteAcceptPage />} />
          <Route path="/quote" element={<QuoteRequestPage />} />
          <Route path="/quote/:id" element={<QuoteRequestPage />} />
          <Route path="/overseas-quote" element={<OverseasQuotePage />} />
          <Route path="/overseas-quote/:id/accept" element={<OverseasQuotePage />} />
          <Route path="/style-quiz" element={<StyleQuizPage />} />
          {/* iter53: primary admin route is /studio-hq (security via obscurity).
              /admin is intentionally a 404 honeypot — bots scanning the standard
              path get nothing. Direct admins to /studio-hq via Header login flow. */}
          <Route path="/studio-hq" element={<ProtectedRoute adminOnly><AdminPage /></ProtectedRoute>} />
          <Route path="/studio-hq/*" element={<ProtectedRoute adminOnly><AdminPage /></ProtectedRoute>} />
        </Routes>
      </Suspense>
      </main>
      {!isAdmin && !isPlay && <Footer />}
      <CartDrawer />
      <CartTracker />
      <EmailHealthAlert />
      <Toaster position="top-right" richColors closeButton />
      {!hideWelcomePopup && <WelcomePopup />}
      {!isAdmin && !isArcade && !isPlay && <PiLauncher />}
      {!isAdmin && !isPlay && <Codebreaker />}
      {!isAdmin && !isCheckout && !isPlay && <GameMasterWidget />}
      {!isAdmin && !isCheckout && !isPlay && <ExitIntentPopup />}
      {!isAdmin && !isCheckout && !isPlay && <InstagramStamp />}
      <AccessibilityToolbar />
      {!isAdmin && !isPlay && <LanguageSwitcher />}
    </>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <SiteContentProvider>
            <MaintenanceProvider>
              <CartProvider>
                <Shell />
              </CartProvider>
            </MaintenanceProvider>
          </SiteContentProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
